import clsx from 'clsx'
import { html, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { DdsElement } from '../../internal/dds-hu-element'
import { IconSize } from '../icon/icon.component'
import styles from './spinner.scss?inline'

export type SpinnerVariant =
  | 'neutral'
  | 'brand'
  | 'negative'
  | 'positive'
  | 'inverted'

/**
 * `dap-ds-spinner`
 * @summary Loading spinner component.
 * @element dap-ds-spinner
 * @title - Spinner
 *
 * @csspart base - The main spinner container.
 * @csspart icon - The loading icon element.
 * @csspart icon-base - The base of the loading icon.
 * @csspart text - The loading text element.
 *
 * @slot - The loading text content.
 *
 * @cssprop --dds-spinner-icon-color-neutral - Color for neutral spinner icons.
 * @cssprop --dds-spinner-icon-color-brand - Color for brand spinner icons.
 * @cssprop --dds-spinner-icon-color-negative - Color for negative spinner icons.
 * @cssprop --dds-spinner-icon-color-positive - Color for positive spinner icons.
 * @cssprop --dds-spinner-icon-color-inverted - Color for inverted spinner icons.
 * @cssprop --dds-spinner-text-spacing - Spacing between spinner and text.
 * @cssprop --dds-spinner-animation-duration - Duration of the spinner animation.
 *
 */
@customElement('dap-ds-spinner')
export default class DapDSSpinner extends DdsElement {
  /**
   * The variant of the spinner.
   * @type {"neutral" | "brand" | "negative" | "positive" | "inverted"}
   */
  @property({ type: String, reflect: true }) variant: SpinnerVariant = 'neutral'
  /**
   * The size of the spinner.
   * @type {"xxl" | "xl" | "lg" | "md" | "sm" | "xs"}
   */
  @property({ type: String, reflect: true }) size: IconSize = 'lg'
  /** The size of the spinner in pixels. This overrides the size attribute */
  @property({ type: Number }) staticSize?: number
  /** The loading text. */
  @property({ type: String }) text: string
  /** Removes the text color */
  @property({ type: Boolean, reflect: true }) noColor = false

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <div
        part="base"
        role="status"
        name="icon-spinner"
        class="${clsx('spinner', {
          [`spinner--${this.size}`]: this.size,
          [`spinner--${this.variant}`]: this.variant,
        })}">
        <dap-ds-icon
          part="icon"
          exportparts="base: icon-base"
          size=${this.size}
          staticSize=${ifDefined(this.staticSize)}
          name="loading-spinner"
          class="spinner__icon"></dap-ds-icon>
        ${this.text
          ? html` <span
              part="text"
              class=${clsx('spinner__text', {
                'spinner__text--no-color': this.noColor,
              })}>
              ${this.text}
            </span>`
          : html`<slot></slot>`}
      </div>
    `
  }
}
